import { normalizeErrorMetadata } from "./logging.js";
const resolveFetch = (config) => {
    if (config.fetch) {
        return config.fetch;
    }
    if (typeof globalThis.fetch === "function") {
        return globalThis.fetch;
    }
    throw new Error("fetch implementation required");
};
const buildQueryString = (query) => {
    if (!query)
        return "";
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined)
            continue;
        searchParams.append(key, String(value));
    }
    const result = searchParams.toString();
    return result ? `?${result}` : "";
};
const randomHex = (bytes) => {
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
        const array = new Uint8Array(bytes);
        crypto.getRandomValues(array);
        return Array.from(array, value => value.toString(16).padStart(2, "0")).join("");
    }
    throw new Error("crypto.getRandomValues is not available");
};
const ensureTraceparent = (existing) => {
    if (existing && existing.trim().length > 0) {
        return existing;
    }
    const traceId = randomHex(16);
    const spanId = randomHex(8);
    return `00-${traceId}-${spanId}-01`;
};
const buildUrl = (baseUrl, path, query) => {
    const trimmedBase = baseUrl.trim();
    const baseWithSlash = trimmedBase.endsWith("/") ? trimmedBase : `${trimmedBase}/`;
    const relativePath = path.startsWith("/") ? path.slice(1) : path;
    const queryString = buildQueryString(query);
    return `${baseWithSlash}${relativePath}${queryString}`;
};
const shouldAttachIdempotency = (include, key) => Boolean(include && key && key.trim().length > 0);
const monotonicNow = () => {
    if (typeof performance !== "undefined" && typeof performance.now === "function") {
        return performance.now();
    }
    return Date.now();
};
const elapsedMs = (startedAt) => {
    const delta = monotonicNow() - startedAt;
    return delta <= 0 ? 0 : Math.round(delta);
};
const logStructuredEvent = (logger, level, event, metadata) => {
    if (!logger) {
        return;
    }
    const payload = { level, event, metadata };
    if (typeof logger.emit === "function") {
        try {
            logger.emit(payload);
            return;
        }
        catch (error) {
            if (typeof console !== "undefined" && typeof console.error === "function") {
                console.error("structured logger emit failed", error);
            }
        }
    }
    try {
        switch (level) {
            case "error":
                logger.error?.(event, metadata);
                break;
            case "warn":
                logger.warn?.(event, metadata);
                break;
            case "debug":
                logger.debug?.(event, metadata);
                break;
            default:
                logger.info?.(event, metadata);
                break;
        }
    }
    catch (error) {
        if (typeof console !== "undefined" && typeof console.error === "function") {
            console.error("structured logger fallback failed", error);
        }
    }
};
/** Extracts tracing and idempotency headers from an incoming request to forward them downstream. */
export const createRequestContext = (request) => ({
    requestId: request.headers.get("x-request-id"),
    traceparent: request.headers.get("traceparent"),
    idempotencyKey: request.headers.get("idempotency-key"),
});
/**
 * Performs a JSON API request with built-in support for structured logging, tracing, and error handling.
 * This is the core function used by all specific API clients.
 */
export async function performJsonRequest(config, options) {
    const fetchImpl = resolveFetch(config);
    const url = buildUrl(config.baseUrl, options.path, options.query);
    const context = options.context ?? {};
    const headers = new Headers();
    let payload;
    if (options.body !== undefined) {
        payload = typeof options.body === "string" ? options.body : JSON.stringify(options.body);
        headers.set("Content-Type", "application/json");
    }
    if (context.requestId && context.requestId.trim().length > 0) {
        headers.set("X-Request-ID", context.requestId.trim());
    }
    const traceparent = ensureTraceparent(context.traceparent);
    headers.set("traceparent", traceparent);
    if (shouldAttachIdempotency(options.includeIdempotencyKey, context.idempotencyKey)) {
        headers.set("Idempotency-Key", context.idempotencyKey.trim());
    }
    // Merge custom headers (e.g., for reCAPTCHA tokens)
    if (options.headers) {
        for (const [key, value] of Object.entries(options.headers)) {
            headers.set(key, value);
        }
    }
    const logger = config.logger;
    const startedAtIso = new Date().toISOString();
    const startedAtMonotonic = monotonicNow();
    const baseCorrelation = {
        requestId: context.requestId ?? null,
        traceparent,
        idempotencyKey: context.idempotencyKey ?? null,
    };
    const buildLogMetadata = (status, overrides = {}) => ({
        method: options.method,
        path: options.path,
        url,
        status,
        startedAt: startedAtIso,
        finishedAt: new Date().toISOString(),
        durationMs: elapsedMs(startedAtMonotonic),
        correlation: { ...baseCorrelation },
        ...overrides,
    });
    // For same-origin BFF proxy requests (relative URLs like /api/bff/...),
    // use 'same-origin' credentials so httpOnly session cookies are sent.
    // For cross-origin backend calls, keep 'omit'.
    const isSameOrigin = !url.startsWith("http://") && !url.startsWith("https://");
    const requestInit = {
        method: options.method,
        headers,
        body: payload,
        mode: isSameOrigin ? 'same-origin' : 'cors',
        credentials: isSameOrigin ? 'same-origin' : 'omit',
    };
    // Only set cache for server-side requests
    if (typeof window === "undefined") {
        requestInit.cache = "no-store";
    }
    let response;
    try {
        response = await fetchImpl(url, requestInit);
    }
    catch (error) {
        logStructuredEvent(logger, "error", "api_client.request.failed", buildLogMetadata(null, {
            outcome: "error",
            failure: {
                kind: "network",
                error: normalizeErrorMetadata(error),
            },
        }));
        throw error;
    }
    const responseHeaders = new Headers(response.headers);
    const upstreamRequestId = response.headers.get("x-request-id");
    if (upstreamRequestId) {
        responseHeaders.set("x-request-id", upstreamRequestId);
    }
    const upstreamTraceparent = response.headers.get("traceparent");
    const upstreamIdempotency = response.headers.get("idempotency-key");
    if (options.includeIdempotencyKey && upstreamIdempotency) {
        responseHeaders.set("idempotency-key", upstreamIdempotency);
    }
    const rawBody = await response.text();
    const contentType = response.headers.get("content-type") ?? "";
    let parsed = undefined;
    if (rawBody.length > 0 && contentType.includes("application/json")) {
        try {
            parsed = JSON.parse(rawBody);
        }
        catch {
            // Ignore JSON parsing errors and fall back to raw text
        }
    }
    responseHeaders.delete("content-length");
    const correlation = {
        ...baseCorrelation,
        upstreamRequestId: responseHeaders.get("x-request-id"),
        upstreamTraceparent: upstreamTraceparent ?? null,
        upstreamIdempotencyKey: upstreamIdempotency ?? null,
    };
    const responseDimensions = {
        contentType,
        bodyBytes: rawBody.length,
    };
    if (response.ok) {
        logStructuredEvent(logger, "info", "api_client.request.succeeded", buildLogMetadata(response.status, {
            outcome: "success",
            correlation,
            response: responseDimensions,
        }));
        return {
            ok: true,
            status: response.status,
            headers: responseHeaders,
            data: parsed ?? undefined,
            rawBody,
        };
    }
    const failure = {
        kind: "http",
        status: response.status,
        contentType,
        bodyBytes: rawBody.length,
    };
    if (typeof parsed === "object" && parsed !== null) {
        failure.parsedBody = parsed;
    }
    else if (rawBody.length > 0) {
        const previewLimit = 1024;
        failure.bodyPreview =
            rawBody.length > previewLimit ? `${rawBody.slice(0, previewLimit)}…` : rawBody;
    }
    logStructuredEvent(logger, "error", "api_client.request.failed", buildLogMetadata(response.status, {
        outcome: "error",
        correlation,
        failure,
    }));
    return {
        ok: false,
        status: response.status,
        headers: responseHeaders,
        rawBody,
        error: parsed,
    };
}
/** Converts a ClientResponse back into a standard `Response` object, suitable for returning from API routes. */
export function toProxyResponse(result) {
    const headers = new Headers(result.headers);
    headers.delete("content-length");
    if (result.rawBody.length > 0) {
        return new Response(result.rawBody, {
            status: result.status,
            headers,
        });
    }
    return new Response(null, {
        status: result.status,
        headers,
    });
}
function filenameFromContentDisposition(header) {
    if (!header) {
        return "comprobante.pdf";
    }
    const utf = /filename\*=UTF-8''([^;]+)/i.exec(header);
    if (utf?.[1]) {
        try {
            return decodeURIComponent(utf[1]);
        }
        catch {
            return utf[1];
        }
    }
    const quoted = /filename="([^"]+)"/i.exec(header);
    if (quoted?.[1]) {
        return quoted[1];
    }
    const plain = /filename=([^;]+)/i.exec(header);
    return plain?.[1]?.trim() || "comprobante.pdf";
}
/**
 * Downloads a binary payload (for example an authenticated tax-document PDF)
 * using the same credentials/correlation headers as JSON requests.
 */
export async function performBlobRequest(config, options) {
    const fetchImpl = resolveFetch(config);
    const url = buildUrl(config.baseUrl, options.path, options.query);
    const context = options.context ?? {};
    const headers = new Headers();
    if (context.requestId && context.requestId.trim().length > 0) {
        headers.set("X-Request-ID", context.requestId.trim());
    }
    const traceparent = ensureTraceparent(context.traceparent);
    headers.set("traceparent", traceparent);
    if (shouldAttachIdempotency(options.includeIdempotencyKey, context.idempotencyKey)) {
        headers.set("Idempotency-Key", context.idempotencyKey.trim());
    }
    const isSameOrigin = !url.startsWith("http://") && !url.startsWith("https://");
    const response = await fetchImpl(url, {
        method: options.method,
        headers,
        mode: isSameOrigin ? "same-origin" : "cors",
        credentials: isSameOrigin ? "same-origin" : "omit",
        cache: typeof window === "undefined" ? "no-store" : undefined,
    });
    const responseHeaders = new Headers(response.headers);
    responseHeaders.delete("content-length");
    if (!response.ok) {
        const rawBody = await response.text();
        let parsed;
        try {
            parsed = JSON.parse(rawBody);
        }
        catch {
            parsed = undefined;
        }
        return {
            ok: false,
            status: response.status,
            headers: responseHeaders,
            rawBody,
            error: parsed,
        };
    }
    const blob = await response.blob();
    return {
        ok: true,
        status: response.status,
        headers: responseHeaders,
        data: {
            blob,
            filename: filenameFromContentDisposition(response.headers.get("content-disposition")),
        },
        rawBody: "",
    };
}
